import React from 'react'
import { Box, Link, Stack, Typography, useTheme } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import RuuviTagDetachButton from '../RuuviTagDetachButton'
import { getHeaterName } from '../../utils/heater'
import { usePolicy } from '../../contexts/PolicyContext'
import type { PairedRuuviTag, Device } from '../../types'

interface RuuviTagAssignmentNoticesProps {
  tag: PairedRuuviTag
  valveDevice?: Device
  projectId: number | null
  onDetachSuccess: (updated: PairedRuuviTag) => void
}

export default function RuuviTagAssignmentNotices({
  tag,
  valveDevice,
  projectId,
  onDetachSuccess
}: RuuviTagAssignmentNoticesProps) {
  const { t } = useTranslation('installation')
  const theme = useTheme()
  const { canViewProject, canEditRuuviTag } = usePolicy()
  const textColor =
    theme.palette.mode === 'light' ? theme.palette.text.secondary : theme.palette.grey[300]
  const noticeRowSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexWrap: 'wrap',
    color: textColor
  }

  const asset = tag.asset ?? undefined
  const derivedProjectId = projectId ?? asset?.projectId ?? null
  const projectDisplayName =
    asset?.project?.name ?? (derivedProjectId ? `#${derivedProjectId}` : undefined)
  const assetTagDevice =
    asset?.ruuviTag?.id === tag.id ? (asset?.ruuviTag?.device ?? undefined) : undefined
  const hostDevice = tag.device ?? assetTagDevice ?? undefined
  const deviceLabel =
    hostDevice?.shortUUID ?? hostDevice?.name ?? tag.deviceName ?? asset?.deviceName ?? undefined
  const isLinkedToHostDevice = Boolean(hostDevice ?? asset?.deviceName)
  const userCanViewPreviousProject = derivedProjectId
    ? canViewProject(asset?.project ?? derivedProjectId)
    : false
  const isValveManaged = Boolean(valveDevice)
  const userCanDetachTag = Boolean(
    derivedProjectId && userCanViewPreviousProject && canEditRuuviTag(tag) && !isValveManaged
  )

  if (!valveDevice && derivedProjectId == null && !deviceLabel) {
    return null
  }

  if (valveDevice) {
    const heaterId = valveDevice.heater?.id ?? valveDevice.heaterId ?? null
    const valveName = valveDevice.name ?? valveDevice.shortUUID ?? null

    const valveHref = valveName ? `/valves/${encodeURIComponent(valveName)}` : null

    let heaterLabel: string | undefined
    if (valveDevice.heater) {
      heaterLabel = getHeaterName(valveDevice.heater)
    }
    if (!heaterLabel) {
      heaterLabel = valveDevice.name ?? valveDevice.shortUUID ?? undefined
    }
    if (!heaterLabel) {
      heaterLabel = t('unknown', { defaultValue: 'Unknown' })
    }

    const message = heaterId
      ? t('valveTagManagedInHeater', {
          heater: heaterLabel,
          defaultValue: 'This tag is linked to heater {{heater}}. Manage it on the heater page.'
        })
      : t('valveTagManagedInHeaterNoLink', {
          defaultValue: 'This tag is linked to a valve device. Manage it on the heater page.'
        })

    return (
      <Stack component="div" spacing={0.5} sx={{ mt: 0.25, color: textColor }}>
        <Box sx={noticeRowSx}>
          <Typography component="span" variant="caption" sx={{ color: 'inherit' }}>
            {message}
          </Typography>
          {valveHref && (
            <Link
              component={RouterLink}
              to={valveHref}
              color="inherit"
              underline="always"
              variant="caption"
              target="_blank"
              rel="noopener"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              {t('openValvePage', { defaultValue: 'Open valve page' })}
            </Link>
          )}
        </Box>
      </Stack>
    )
  }

  return (
    <Stack component="div" spacing={0.5} sx={{ mt: 0.25, color: textColor }}>
      {derivedProjectId != null && (
        <>
          <Box sx={noticeRowSx}>
            {userCanViewPreviousProject ? (
              <Link
                component={RouterLink}
                to={`/projects/${derivedProjectId}`}
                color="inherit"
                sx={{ color: 'inherit' }}
                underline="always"
                variant="caption"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.stopPropagation()
                  }
                }}
              >
                {t('assignedToProjectNote', {
                  project: projectDisplayName,
                  defaultValue: 'Assigned to project {{project}}'
                })}
              </Link>
            ) : (
              <Typography component="span" variant="caption" sx={{ color: 'inherit' }}>
                {t('assignedToProjectNote', {
                  project: projectDisplayName,
                  defaultValue: 'Assigned to project {{project}}'
                })}
              </Typography>
            )}

            {userCanDetachTag && (
              <Box
                sx={{ display: 'inline-flex' }}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <RuuviTagDetachButton
                  ruuviTag={tag}
                  size="small"
                  variant="outlined"
                  color="primary"
                  buttonText={t('ruuvitag:detachFromProject')}
                  afterSubmit={onDetachSuccess}
                />
              </Box>
            )}
          </Box>

          {!userCanViewPreviousProject &&
            (isLinkedToHostDevice ? (
              <Box sx={noticeRowSx}>
                <Typography component="span" variant="caption" sx={{ color: 'inherit' }}>
                  {t('tagAccessDeniedNeedDetach', {
                    project: projectDisplayName,
                    device: deviceLabel ?? t('unknown', { defaultValue: 'Unknown' })
                  })}
                </Typography>
              </Box>
            ) : (
              <Box sx={noticeRowSx}>
                <Typography component="span" variant="caption" sx={{ color: 'inherit' }}>
                  {t('tagAutoReassignNotice', {
                    project: projectDisplayName
                  })}
                </Typography>
              </Box>
            ))}
        </>
      )}

      {deviceLabel && (
        <Box sx={noticeRowSx}>
          <Typography component="span" variant="caption" sx={{ color: 'inherit' }}>
            {t('assignedToDeviceNote', {
              device: deviceLabel,
              defaultValue: 'Assigned to device {{device}}'
            })}
          </Typography>
        </Box>
      )}
    </Stack>
  )
}
